import MuiAutocomplete from '@mui/material/Autocomplete';
import MuiBox from '@mui/material/Box';
import MuiFormControl from '@mui/material/FormControl';
import MuiFormLabel from '@mui/material/FormLabel';
import MuiPaper, { type PaperProps } from '@mui/material/Paper';
import MuiTextField from '@mui/material/TextField';
import { createContext, useContext, useId, type ReactNode } from 'react';

export interface AutocompleteOption {
  id: string | number;
  label: string;
}

export interface AutocompleteProps {
  // Visible caption, drawn ABOVE the control and wired to the input by a real `<label for>`.
  // Optional because a caller may already be drawing it — the account form's rows caption
  // themselves, in their own treatment — in which case `aria-label` names the control instead.
  label?: string;
  // The name for a control whose caption is somebody else's. Prefer `label` wherever the text is
  // on screen: one text, not two to keep in sync. One of the two is always needed — a nameless
  // combobox is a control assistive tech can reach and cannot introduce.
  'aria-label'?: string;
  options: AutocompleteOption[];
  value: AutocompleteOption | null;
  onChange: (option: AutocompleteOption | null) => void;
  inputValue: string;
  onInputChange: (text: string) => void;
  // The glyph the field opens with — the account form's magnifier, which is what says «type to
  // search» here now that the chevron is gone (see `forcePopupIcon` below). A prop and not a
  // hardcoded icon: a combobox is not necessarily a search box, and the next caller picks its own.
  // Named as in `TextField`, so a view hands the same node to either control.
  startAdornment?: ReactNode;
  // Draws the asterisk AND writes the attribute assistive tech reads the state from. Cheap here,
  // unlike in `Select`: MUI's combobox IS a native <input>, so nothing has to hand-route
  // `aria-required` onto a div.
  required?: boolean;
  disabled?: boolean;
  // Its options are still being read. Distinct from `disabled` in meaning and identical in
  // effect — the same pair, for the same reason, as in `Select`: an enabled combobox holding
  // nothing reads as «there are no addresses», a different fact the caller renders differently.
  loading?: boolean;
  fullWidth?: boolean;
  // The persistent, always-announced companion to the caption. This field's live use for it is
  // the read-failed state, where it is the ONLY signal that the address cannot be changed now —
  // and it goes here and never in `error`, because a field working as designed is not invalid.
  helperText?: string;
  // Something is wrong with the value. A STRING that replaces `helperText` rather than joining
  // it — `Select`'s idiom, not `TextField`'s boolean — because two descriptions on one control is
  // a queue nobody asked for, and a boolean lets a field be marked invalid with no message.
  error?: string;
  // How many rows may be PAINTED. Not how many are read: the suggestions read neither caps nor
  // paginates, and a three-letter term legitimately matches hundreds of buildings. The cap is
  // what keeps the dropdown readable; `footer` is what keeps it honest about the rest.
  limit?: number;
  // The dropdown's last row — the line naming the true total. A row and not a `helperText`: it
  // describes the LIST, not the control, and it disappears with the list.
  footer?: ReactNode;
  // What the dropdown says when the answer came back empty. Left to MUI's English default when
  // omitted, so a caller that forgets it is visibly wrong rather than quietly blank.
  noOptionsText?: string;
  // Which step of the type scale the control's TEXT is set in. `body1` (MUI's own default for every
  // `InputBase`) unless a caller asks otherwise; `body2` is for a field that REPLACES body2 copy in
  // place — the «Usuario» ficha's in-line edit mode, where a 16px control in a column of 14px
  // values re-types the whole column one step bigger. The `size="small"` this wrapper already sets
  // does NOT cover it: that trims padding and leaves the type step alone. Mirrors `TextField`'s
  // prop of the same name, so a view hands the same value to either control.
  textVariant?: 'body1' | 'body2';
}

// Deliberately ABSENT from that list: `freeSolo`. Mandatory selection is this control's reason
// to exist — it is what guarantees that whatever a caller holds is a real option with a real id,
// and what makes MUI put the term back on blur instead of leaving a half-typed fragment. A prop
// that switches it off would be a way to lose the guarantee by accident.

// Travels by context rather than by closure so the paper component below can stay module-level
// and STABLE: `slots.paper` is a component type, and handing MUI a fresh identity on every
// render would remount the dropdown on every keystroke — losing its scroll position mid-list.
// Context reaches it even though the popper renders into a portal: portals move DOM, not the
// React tree.
// `body2` for the control and for its dropdown rows. Two selectors because the popper renders into
// a PORTAL: an `sx` on the control cannot reach the list, so the listbox takes its own.
const BODY_TWO_FIELD_SX = { '& .MuiInputBase-root': { typography: 'body2' } } as const;
const BODY_TWO_SX = { typography: 'body2' } as const;

const FooterContext = createContext<ReactNode>(null);

// The row's box, not its words: the caller owns what the line says (a façade `Text`), the
// wrapper owns where it sits. A rule on top because it is NOT one of the suggestions — the same
// line that separates the rows, doing the opposite job.
const FOOTER_ROW_SX = {
  px: 2,
  py: 1,
  borderTop: '1px solid',
  borderColor: 'divider',
} as const;

// The design captures rule a line between the suggestions (2026-09-11): in a list of addresses
// that differ by two digits, the separation is what makes the rows countable. Never under the
// last one — the paper's edge already ends the list, and a rule there reads as «one more row
// below», which is precisely the lie the footer exists to tell truthfully.
const ROW_RULES_SX = {
  '& .MuiAutocomplete-option:not(:last-of-type)': {
    borderBottom: '1px solid',
    borderColor: 'divider',
  },
} as const;

function PaperWithFooter({ children, ...props }: PaperProps) {
  const footer = useContext(FooterContext);
  return (
    <MuiPaper {...props}>
      {children}
      {/* OUTSIDE the listbox on purpose: the line is not selectable, and a non-option child of
          `role="listbox"` is invalid ARIA — the contract MUI implements for us is the only
          reason this control is an Autocomplete instead of a hand-rolled menu. */}
      <MuiBox sx={FOOTER_ROW_SX}>{footer}</MuiBox>
    </MuiPaper>
  );
}

// The label sits ABOVE the control rather than floating into the outline notch, so the field's
// border stays unbroken. Kept identical to the `TextField` and `Select` captions on purpose — in
// this feature's form all three stand in one column, and a caption a step larger on one of them
// would misalign the set.
const ABOVE_LABEL_SX = {
  typography: 'body2',
  mb: 1,
  // A label ON the control doubles as a focus indicator; one above it is a caption, and the
  // control's own focus ring already says where focus is. Explicit because this one DOES hear
  // about focus — it sits inside the FormControl — and MUI would otherwise tint it primary.
  '&.Mui-focused': { color: 'text.secondary' },
} as const;

// MUI renders a helper at `text.secondary` — #ACA5BB, 2.37:1 on white, under AA for body text —
// and reddens it to `error.main` in the error state, which at 3.09:1 is no better: that figure
// clears the 3:1 an edge needs and falls short of the 4.5:1 a sentence does. A description here
// is a rule the agent has to act on, so it keeps the body tone in BOTH states and the meaning
// travels in the words and in the frame, never in the colour. Same call, same numbers, as the
// `TextField` wrapper — the two stand in one column in the account form.
const HELPER_TONE_SX = {
  '& .MuiFormHelperText-root': { color: 'text.primary' },
  // Said again for the error state: MUI's own `.Mui-error` rule matches at the same weight as the
  // unqualified one above and would win on injection order alone.
  '& .MuiFormHelperText-root.Mui-error': { color: 'text.primary' },
} as const;

export function Autocomplete({
  label,
  options,
  value,
  onChange,
  inputValue,
  onInputChange,
  startAdornment,
  required = false,
  disabled = false,
  loading = false,
  fullWidth = false,
  helperText,
  error,
  limit,
  footer,
  noOptionsText,
  textVariant = 'body1',
  'aria-label': ariaLabel,
}: AutocompleteProps) {
  // Own id rather than MUI's generated one: the caption is rendered by this wrapper, outside the
  // control, so `htmlFor` has to point at something this wrapper knows the name of. Handed to MUI
  // as the field's `id`, which is what it hangs the input's own id off.
  const inputId = useId();
  // One state under two names. Resolved once, here, so the FormControl, the combobox and the
  // field it renders cannot drift apart.
  const blocked = disabled || loading;
  // The error outranks the helper text: one description, and the urgent one wins.
  const description = error ?? helperText;

  return (
    // size="small" is hardcoded to match the TextField and Select wrappers: the three sit in one
    // column in the account form and would otherwise stand at different heights.
    <MuiFormControl size="small" required={required} disabled={blocked} fullWidth={fullWidth}>
      {label !== undefined && (
        // FormLabel, not InputLabel: the latter is the FLOATING label — it positions itself over
        // the control and shrinks into the outline's notch. This one is a static caption above the
        // field, which is what FormLabel already is.
        <MuiFormLabel htmlFor={inputId} sx={ABOVE_LABEL_SX}>
          {label}
        </MuiFormLabel>
      )}
      <FooterContext.Provider value={footer ?? null}>
        <MuiAutocomplete
          id={inputId}
          disabled={blocked}
          fullWidth={fullWidth}
          options={options}
          value={value}
          getOptionLabel={(option) => option.label}
          // By `id`, never by object identity — MUI's default is `===`. A caller may hand a value it
          // BUILT rather than one it picked from `options` (the account form seeds this field with a
          // stand-in option made from the ficha's address text, whose spelling is not even the same
          // as the suggestions read's), and under `===` MUI would call that a different thing: no
          // row marked as selected, and a console warning on every render.
          isOptionEqualToValue={(option, selected) => option.id === selected.id}
          onChange={(_event, option) => {
            onChange(option);
          }}
          // Controlled in both axes, and deliberately so: the term drives a debounced server read
          // that lives in the caller, and the caller is the only thing that knows what it asked for.
          inputValue={inputValue}
          onInputChange={(_event, text) => {
            onInputChange(text);
          }}
          // The wrapper filters NOTHING, it only CAPS. MUI's default matches the option labels
          // against what is typed, which here is wrong twice over: the options already ARE the
          // answer to the term (a debounced server read, not a local catalogue), so a second match
          // silently drops any row the server matched on something the label does not spell — and
          // blanks the list for a keystroke every time the term runs ahead of the answer in flight.
          // Hence the identity function rather than `createFilterOptions({ limit })`, which would
          // have brought the filtering back in with the cap.
          filterOptions={(rows) => (limit === undefined ? rows : rows.slice(0, limit))}
          {...(footer !== undefined ? { slots: { paper: PaperWithFooter } } : {})}
          {...(noOptionsText !== undefined ? { noOptionsText } : {})}
          slotProps={{ listbox: { sx: [ROW_RULES_SX, ...(textVariant === 'body2' ? [BODY_TWO_SX] : [])] } }}
          // On the InputBase ROOT, where MUI sets `body1` and where the adornments live: a step
          // applied to the inner input alone would leave the magnifier sized for the other scale
          // beside it. The dropdown rows take it too — a list set one step above the field it
          // answers reads as a different control's list. SPREAD and not passed: with
          // `exactOptionalPropertyTypes` an explicit `undefined` is not the same as absent.
          {...(textVariant === 'body2' ? { sx: BODY_TWO_FIELD_SX } : {})}
          // No chevron. The design (captures, 2026-09-11) gives this field a magnifier instead, and
          // `false` REMOVES the button rather than emptying it: `popupIcon={null}` would leave a
          // glyph-less button sitting in the tab order, which is worse than either option.
          forcePopupIcon={false}
          // MUI's own TextField, not the façade's: we are INSIDE the façade here, and the façade's
          // wrapper renders a caption and a FormControl of its own that this one already provides.
          // `InputLabelProps` is dropped, not forwarded: it exists to drive MUI's FLOATING label,
          // and this wrapper renders its caption above the control instead. (Under
          // `exactOptionalPropertyTypes` it does not even typecheck — its `className` is optional
          // here and required there.)
          renderInput={({ InputProps, inputProps, InputLabelProps: _label, ...params }) => (
            <MuiTextField
              {...params}
              size="small"
              // Asked for again here rather than inherited: MUI's TextField opens a FormControl of
              // its own, so the outer one's state reaches the caption and nothing else. The
              // asterisk is the outer one's job; the attribute on the input is this one's.
              {...(required ? { required } : {})}
              // Rendered by MUI's own field and not as a sibling of it: that is what buys the
              // `aria-describedby` wiring, which is the whole point of a description.
              {...(description !== undefined ? { helperText: description, sx: HELPER_TONE_SX } : {})}
              // The tint lands on the frame and writes `aria-invalid` onto the input. NOT set on
              // the outer FormControl: there it would redden the caption too (see HELPER_TONE_SX).
              {...(error !== undefined ? { error: true } : {})}
              slotProps={{
                // The name goes on the <input> and not on the control root: MUI spreads unknown
                // props onto the root, where a name would land on a div nothing points at. MUI's
                // own `inputProps` are re-spread here because `slotProps.htmlInput` REPLACES them
                // — and they carry the combobox's `aria-activedescendant` and `aria-controls`.
                htmlInput: {
                  ...inputProps,
                  ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}),
                },
                input: {
                  // MUI's own — the ref the popper anchors to, and the end adornment holding the
                  // clear control. Spread FIRST so the caller's glyph joins them instead of
                  // replacing the lot.
                  ...InputProps,
                  ...(startAdornment !== undefined ? { startAdornment } : {}),
                },
              }}
            />
          )}
        />
      </FooterContext.Provider>
    </MuiFormControl>
  );
}
